import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { getReferralStatus } from "../lib/api";

const STATUS_META = {
  New: { icon: Clock, tone: "text-gold-deep", bg: "bg-gold/10", label: "Received — awaiting review" },
  "In Review": { icon: Loader2, tone: "text-navy", bg: "bg-navy/5", label: "Under review by our intake team" },
  Contacted: { icon: CheckCircle2, tone: "text-teal", bg: "bg-teal/10", label: "Veteran contacted" },
  Placed: { icon: CheckCircle2, tone: "text-teal", bg: "bg-teal/10", label: "Placed into housing" },
  Closed: { icon: CheckCircle2, tone: "text-slate", bg: "bg-softgray/40", label: "Closed" },
};

export const ReferralStatusLookup = () => {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    if (!ref.trim()) { setError("Enter your confirmation number."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const data = await getReferralStatus(ref.trim());
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "No referral found with that confirmation number.");
    } finally { setLoading(false); }
  };

  const meta = result ? (STATUS_META[result.status] || STATUS_META.New) : null;
  const StatusIcon = meta?.icon || Clock;

  return (
    <div className="i-card bg-offwhite p-8" data-testid="referral-lookup">
      <div className="label-caps text-navy mb-2">Check a referral</div>
      <p className="text-sm text-charcoal/75 leading-relaxed mb-5">Already referred a veteran? Enter your confirmation number to see where it stands.</p>
      <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate" />
          <input
            value={ref}
            onChange={(e) => { setRef(e.target.value.toUpperCase()); setError(""); }}
            placeholder="e.g. ISR-1A2B3C4D"
            data-testid="referral-lookup-input"
            className="w-full rounded-md border border-softgray bg-white pl-10 pr-4 py-3 text-sm text-charcoal placeholder:text-slate/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/30"
          />
        </div>
        <button type="submit" disabled={loading} data-testid="referral-lookup-btn" className="inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-6 py-3 rounded-md text-sm font-semibold hover:bg-navy-deep transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check status"}
        </button>
      </form>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div key="err" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4 flex items-center gap-2 text-sm text-charcoal/80" data-testid="referral-lookup-error">
            <AlertCircle className="h-4 w-4 text-slate shrink-0" /> {error}
          </motion.div>
        )}
        {result && (
          <motion.div key="res" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-5 rounded-lg border border-softgray bg-white p-5" data-testid="referral-lookup-result">
            <div className="flex items-center justify-between gap-3">
              <span className="font-heading text-sm font-black text-navy">{result.ref_id}</span>
              {result.urgency && <span className="text-xs font-semibold text-slate uppercase tracking-wide">{result.urgency} urgency</span>}
            </div>
            <div className={`mt-3 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold ${meta.bg} ${meta.tone}`}>
              <StatusIcon className={`h-4 w-4 ${result.status === "In Review" ? "animate-spin" : ""}`} /> {meta.label}
            </div>
            <p className="mt-3 text-sm text-charcoal/70">
              Referral for {result.veteran_first_name} {result.veteran_last_initial} · submitted {new Date(result.created_at).toLocaleDateString()}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferralStatusLookup;
